import React, { useMemo, useState, useCallback, useEffect } from "react";
import TabGridAndFormLayout from "../../../Components/ProForms/ProFormsLayout/TabGridAndFormLayout";
import ProGrid from "../../../Components/ProGrid/v2";
import { changeMode, changeModeForField, fieldTruePermission, prepareDefaultValues, prepareInitialConfig } from "../../../../utils/formHelper";
import useColumn from "../../../../utils/customHooks/useColumns";
import usePageState from "../../../../utils/customHooks/usePageState";
import { useForm } from "react-hook-form";
import { STAUTES } from "../../../../utils/constant";
import ProTextInput from "../../../Components/Inputs/TextField";
import { createODataQuery } from "../../../../utils/services/oData";
import { deleteApi, getApi, postApi, putApi } from "../../../../utils/services";
import { ADMIN_RAP_WPS_ACTIVITIES } from "../../../../utils/services/apiPath";
import { useParams } from "react-router-dom";
import { errorToast, successToast } from "../../../../utils/toastHelper";
import NameCell from "../../../Components/ProGrid/components/NameCell";
import DeleteCell from "../../../Components/ProGrid/components/DeleteCell";
import usePermission from "../../../../utils/customHooks/usePermission";
import { apprenticeRapPermissionSelector } from "../../../store/features/permissions/permissionsSlice";

const WPSActivitiesFormConfing = {
  wpsTitle: {
    label: "WPS Title",
    rules: { required: "WPS Title is required!" }
  },
  wpsDescription: {
    label: "WPS Description",
    fieldProps: { multiline: true, rows: 4 },
    rules: { required: "WPS Description is required!" }
  },
  requiredHours: {
    label: "Required Hours",
    rules: { required: "Required Hours is required!" }
  }
};
export default function WpsActivities() {
  const { id } = useParams();
  const [showGrid, setShowGrid] = useState(true);
  const [formConfig, setFormConfig] = useState(prepareInitialConfig(WPSActivitiesFormConfing));
  const [wpsActivitiesData, setWpsActivitiesData] = useState({});
  const readOnly = localStorage.getItem("viewingReadOnlyRapInfo") == "true";
  const [actionData, setActionData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const {
    currentPage,
    pageSize,
    data: wpsData,
    rowCount,
    status,
    sortModel,
    searchString,
    setPageState,
    setPagination,
    setStatus,
    setSortModel,
    setSearchString
  } = usePageState();
  const { "WPS Activities": rapWpsActivityPermission } = usePermission(apprenticeRapPermissionSelector, "WPS Activities");
  const { canView, canAdd, canEdit, canDelete, canExport } = rapWpsActivityPermission || {};

  const columns = useColumn(
    [
      {
        field: "wpsTitle",
        headerName: "Title",
        renderCell: params => <NameCell params={params} onClick={onUserClick} />
      },
      {
        field: "wpsDescription",
        headerName: "Description"
      },
      {
        field: "requiredHours",
        headerName: "Required Hours"
      },
      {
        field: "rapWpsActivityId",
        headerName: "Actions",
        isDeleteColumn: true,
        disableExport: true,
        width: 80,
        renderCell: params => <DeleteCell title={"Delete WPS Activities"} params={params} onDelete={onDelete} disabled={readOnly} />
      }
    ],
    canDelete
  );
  const { handleSubmit, control, setValue } = useForm({ defaultValues: prepareDefaultValues(WPSActivitiesFormConfing) });
  const isEditModeOn = useMemo(() => Object.values(formConfig).some(({ mode }) => mode === "edit"), [formConfig]);

  const onEdit = () => setFormConfig(changeMode("edit"));
  const handleReadValueClick = name => setFormConfig(changeModeForField(name, "edit"));

  const resetForm = () => {
    setActionData({});
    setShowGrid(true);
    setFormConfig(prev => Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: { ...prev[key], mode: "read" } }), {}));
  };

  const onCancel = () => {
    resetForm();
    setShowGrid(true);
  };

  const onUserClick = params => {
    setShowGrid(false);
    setActionData(params.row);
    getClientUserData(params.row);
  };

  const addButtonAction = () => {
    Object.keys(formConfig).forEach(field => setValue(field, null));
    onEdit();
    setShowGrid(false);
  };

  const onSave = async data => {
    if (isSaving) return;
    setIsSaving(true);
    const payload = {
      wpsTitle: data?.wpsTitle,
      wpsDescription: data?.wpsDescription,
      requiredHours: parseInt(data?.requiredHours),
      rapInfoId: id
    };
    const methodApi = actionData?.rapWpsActivityId ? putApi : postApi;
    const { error } = await methodApi(
      actionData?.rapWpsActivityId ? `${ADMIN_RAP_WPS_ACTIVITIES}/${actionData.rapWpsActivityId}` : ADMIN_RAP_WPS_ACTIVITIES,
      payload
    );
    if (error) return errorToast(error);
    getAllWPSActivities();
    resetForm();
    setIsSaving(false);
    successToast(`WPS Activities ${actionData?.rapwpsActivityId ? "updated" : "added"} successfully.`);
  };

  const onDelete = async id => {
    const { error } = await deleteApi(`${ADMIN_RAP_WPS_ACTIVITIES}/${id}`);
    if (error) return errorToast(error);
    getAllWPSActivities();
    setShowGrid(true);
    successToast("WPS Activities deleted successfully.");
  };

  const getClientUserData = updateData => {
    const data = {
      wpsTitle: updateData?.wpsTitle,
      wpsDescription: updateData?.wpsDescription,
      requiredHours: updateData?.requiredHours
    };
    setWpsActivitiesData(data);
    Object.keys(formConfig).forEach(field => setValue(field, data[field]));
  };

  const getAllWPSActivities = useCallback(async () => {
    setStatus(STAUTES.LOADING);
    const searchableFields = ["wpsTitle", "wpsDescription", "cast(requiredHours, 'Edm.String')"];
    const { data, totalRecords, error } = await getApi(
      ADMIN_RAP_WPS_ACTIVITIES + `${createODataQuery(searchableFields, [], "", searchString, sortModel, pageSize, currentPage)}&rapInfoId=${id}`
    );
    setStatus(STAUTES.IDLE);
    if (error) return errorToast(error);
    setPageState(prevPageInfo => ({ ...prevPageInfo, data: data || [], rowCount: totalRecords }));
  }, [currentPage, pageSize, sortModel, searchString, setPageState, setStatus]);

  useEffect(() => {
    getAllWPSActivities();
  }, [getAllWPSActivities]);

  const defaultFormProps = { control, formValues: wpsActivitiesData, handleReadValueClick, permission: { canView, canAdd, canEdit } };
  return (
    <TabGridAndFormLayout
      showGrid={showGrid}
      title={`${actionData?.rapwpsActivityGuid ? "Edit" : "Add"} WPS Activities`}
      backAction={() => resetForm()}
      beingEdited={isEditModeOn}
      onEdit={onEdit}
      backLabel={"Go Back to WPS Activities"}
      canView={canView}
      hideEdit={readOnly || !canEdit}
      onSave={handleSubmit(onSave)}
      onCancel={onCancel}
      isSaving={isSaving}
    >
      {showGrid && (
        <ProGrid
          title={`WPS Activities`}
          loading={status === STAUTES.LOADING}
          options={{
            getRowId: row => row.rapWpsActivityId,
            rowCount: rowCount,
            paginationMode: "server",
            paginationModel: { pageSize: pageSize, page: currentPage },
            onPaginationModelChange: setPagination,
            sortingMode: "server",
            onSortModelChange: setSortModel
          }}
          columns={columns}
          rows={wpsData}
          searchMode={!!searchString}
          searchModel={{ defaultValue: searchString, handleDebounce: setSearchString }}
          addButtonLabel="+ Add WPSActivity"
          hideExport={!canExport}
          hideAddButton={!canAdd}
          addButtonAction={addButtonAction}
        />
      )}
      {!showGrid && (
        <>
          <ProTextInput {...defaultFormProps} {...formConfig.wpsTitle} />
          <ProTextInput {...defaultFormProps} {...formConfig.requiredHours} type="number" />
          <ProTextInput {...defaultFormProps} {...formConfig.wpsDescription} />
        </>
      )}
    </TabGridAndFormLayout>
  );
}
